import { isOwnerRoleKey, normalizeRoleKey, type IRole } from '@/domain/entities/role';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ErrorNotice } from '@/components/ui/error-notice';
import { LoadingSpinner } from '@/components/ui/loading-spinner';

type OrganizationRolesTabProps = {
  roles: IRole[];
  orgCapabilities: string[];
  isLoading: boolean;
  hasFetched: boolean;
  error: string | null;
  onRetry: () => void;
};

function formatRoleKey(sk: string) {
  return normalizeRoleKey(sk);
}

function isOwnerRole(sk: string) {
  return isOwnerRoleKey(sk);
}

function formatPermissionsCount(role: IRole, orgCapabilitiesCount: number) {
  const count = role.isFixed && isOwnerRole(role.SK) ? orgCapabilitiesCount : (role.permissions?.length ?? 0);
  return `${count} permission${count === 1 ? '' : 's'}`;
}

function RolesSection({
  title,
  description,
  roles,
  orgCapabilitiesCount,
}: {
  title: string;
  description: string;
  roles: IRole[];
  orgCapabilitiesCount: number;
}) {
  return (
    <section className="space-y-3">
      <div>
        <h4 className="text-sm font-semibold">{title}</h4>
        <p className="text-muted-foreground text-xs">{description}</p>
      </div>

      {roles.length === 0 ? (
        <p className="text-muted-foreground text-sm">No roles found.</p>
      ) : (
        <div className="space-y-2">
          {roles.map((role) => (
            <div key={`${role.PK}|${role.SK}`} className="rounded-md border p-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="space-y-1">
                  <p className="text-sm font-medium">{role.name}</p>
                  <p className="text-muted-foreground text-xs">
                    key: <code>{formatRoleKey(role.SK)}</code>
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={role.isFixed ? 'default' : 'secondary'}>{role.isFixed ? 'Fixed' : 'Custom'}</Badge>
                  <Badge variant="outline">{formatPermissionsCount(role, orgCapabilitiesCount)}</Badge>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export function OrganizationRolesTab({
  roles,
  orgCapabilities,
  isLoading,
  hasFetched,
  error,
  onRetry,
}: OrganizationRolesTabProps) {
  const fixedRoles = roles.filter((role) => role.isFixed);
  const customRoles = roles.filter((role) => !role.isFixed);
  const orgCapabilitiesCount = orgCapabilities.length;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Roles</CardTitle>
        <CardDescription>All fixed and custom roles in this organization scope.</CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {!hasFetched || isLoading ? (
          <div className="text-muted-foreground flex min-h-[200px] flex-col items-center justify-center gap-2 text-sm">
            <LoadingSpinner size={40} />
            <p>Loading roles…</p>
          </div>
        ) : error ? (
          <div className="space-y-3">
            <ErrorNotice message={error} title="Failed to Load Roles" />
            <Button type="button" variant="outline" onClick={onRetry}>
              Retry
            </Button>
          </div>
        ) : roles.length === 0 ? (
          <p className="text-muted-foreground text-sm">No roles found for this organization.</p>
        ) : (
          <>
            <RolesSection
              title="Fixed Roles"
              description="System roles that are always available in this scope."
              roles={fixedRoles}
              orgCapabilitiesCount={orgCapabilitiesCount}
            />
            <RolesSection
              title="Custom Roles"
              description="Organization-specific roles created for this scope."
              roles={customRoles}
              orgCapabilitiesCount={orgCapabilitiesCount}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}
